/* TASK-1735 — Footer "Track your order" interactive widget.
 * TASK-1740 — Block is always open (no toggle/chevron), so this script only
 *             wires input + button + error state. Carrier-detection logic
 *             below is mirrored from ils-admin/carriers.js v1.0.45 (single
 *             source of truth). If carriers.js changes, mirror it here too.
 * TASK-5011 — FedEx added, mirroring the carriers.js `fedex` row. FedEx is the
 *             courier for the 23 courier-only EU countries; every live FedEx
 *             number is 12 digits beginning `87` (41/41 fulfilments over
 *             2026-09-29..30) and Shopify already publishes them on
 *             tracking.swotzy.com. The `87` prefix is pinned deliberately —
 *             a generic /^\d{12}$/ would claim unrelated 12-digit input and
 *             open a URL that 404s.
 *
 * TASK-5012 — universal Klaviyo deep link. One URL shape,
 *             `<any localized path>?tracking=<code>`, resolves the carrier on
 *             the site instead of in the email, so Klaviyo never needs carrier
 *             logic. The query flow reuses normalize/detectCarrier/
 *             buildTrackingLink below verbatim — there is deliberately no
 *             second carrier table and no carrier-specific branch in it. The
 *             automatic redirect uses location.replace(), NOT window.open():
 *             a popup opened from page load (no user gesture) is blocked by
 *             every mainstream browser, and replace() additionally keeps the
 *             URL that carries the customer's tracking number out of the back
 *             stack. The MANUAL Track button is untouched and still opens a
 *             new tab, because there a real click authorises the popup.
 *
 * TASK-5574 — the `unisend` row now matches BOTH prefixes the SAME operator
 *             mints: HC+9+LT and CH+9+LT. Measured on the whole live
 *             tracking_index population (2026-10-17): 241 HC and 5 CH, with
 *             Shopify reporting trackingInfo.company = "Unisend" on 5 of 5 of
 *             the CH ones. Before the alias those 5 were the ONLY unrecognised
 *             shape in the registry, so this widget answered "we could not
 *             recognise this code" for real parcels. Listed as an EXPLICIT
 *             two-prefix alternation rather than widened to [A-Z]{2}: only
 *             these two have evidence (both are UPU S10 service indicators),
 *             and claiming the other 674 would start matching other
 *             operators' numbers.
 *
 * TASK-5579 — SERVER FALLBACK FOR A NUMBER NO PATTERN CAN MATCH.
 *
 *             The shipping router may now choose Swotzy courier services whose
 *             published number format is BARE DIGITS: DHL Express issues a
 *             10-digit air waybill and DPD a 14-digit parcel number. There is
 *             no honest narrow pattern for either — /^\d{10}$/ would claim
 *             unrelated input, and a prefix-anchored variant would be an
 *             invented claim about our own series. So no pattern was added for
 *             them, deliberately, and this widget instead ASKS THE SERVER:
 *
 *               GET {apiBase}/api/delivery/tracking-resolve?number=<EXACT>
 *                 -> { found, carrier, tracking_url }
 *
 *             The server answers from OUR OWN shipment record by EXACT match,
 *             so it can identify any parcel we booked without any pattern at
 *             all, and it returns nothing about the order or the customer —
 *             at most a carrier name and an official portal URL.
 *
 *             ORDER OF OPERATIONS IS UNCHANGED FOR EVERY NUMBER THAT ALREADY
 *             WORKED. The seven patterns are tried FIRST and answer instantly,
 *             offline, exactly as before; the network call happens only when
 *             they all miss — i.e. only in the case that previously showed an
 *             error. A failed or slow lookup falls back to that same existing
 *             error state, so the widget can only get better, never worse.
 *
 * INTERNAL platform keys ("swotzy", "zipasgo") are used only inside this file
 * to pick the right URL template. They MUST NEVER be rendered as UI text.
 */
(function () {
  'use strict';

  var CARRIERS = [
    { id: 'omniva',     pattern: /^CC\d{9}EE$/i,        platform: 'swotzy'  },
    { id: 'unisend',    pattern: /^(?:HC|CH)\d{9}LT$/i, platform: 'swotzy'  },
    { id: 'dpd_baltic', pattern: /^05768570\d{6}$/,     platform: 'swotzy'  },
    { id: 'venipak',    pattern: /^V30923E\d{7}$/i,     platform: 'swotzy'  },
    { id: 'nova_post',  pattern: /^SHLV\d{10}$/i,       platform: 'swotzy'  },
    { id: 'fedex',      pattern: /^87\d{10}$/,          platform: 'swotzy'  },
    { id: 'dhl',        pattern: /^ZP\d{13}$/i,         platform: 'zipasgo' }
  ];

  function normalize(tn) {
    return String(tn || '').replace(/\s+/g, '').toUpperCase();
  }

  function detectCarrier(tn) {
    if (!tn) return null;
    for (var i = 0; i < CARRIERS.length; i++) {
      if (CARRIERS[i].pattern.test(tn)) return CARRIERS[i];
    }
    return null;
  }

  function buildTrackingLink(tn) {
    var c = detectCarrier(tn);
    if (!c) return null;
    if (c.platform === 'swotzy')  return 'https://tracking.swotzy.com/' + encodeURIComponent(tn);
    if (c.platform === 'zipasgo') return 'https://my.zipasgo.com/tracking/' + encodeURIComponent(tn);
    return null;
  }

  /* ------------------------------------------- TASK-5579 server resolution */

  /* Same apiBase convention as custom-pdp-delivery-days-live.js, so there is
     one place to change the origin and one default. */
  function apiBase() {
    var cfg = window.ILS_PDP || window.ILS_PAGE || {};
    var b = (typeof cfg.apiBase === 'string' && cfg.apiBase) ? cfg.apiBase : '';
    return (b || 'https://alert-dedication-production.up.railway.app').replace(/\/+$/, '');
  }

  /* The shape the server will consider at all. Mirrors the server's own token
     gate so we never spend a request on input that cannot be a number. */
  var TOKEN_RE = /^[A-Z0-9]{6,40}$/;

  /* A slow backend must not leave the buyer staring at a dead button, so the
     lookup is abandoned after 4 s and the existing error state takes over. */
  var RESOLVE_TIMEOUT_MS = 4000;

  /* Returns a Promise of a URL string, or null. NEVER rejects: every failure
     path resolves to null so the caller has exactly one thing to handle. */
  function resolveViaServer(tn) {
    if (!TOKEN_RE.test(tn)) return Promise.resolve(null);
    if (typeof window.fetch !== 'function') return Promise.resolve(null);

    var url = apiBase() + '/api/delivery/tracking-resolve?number=' + encodeURIComponent(tn);
    var settled = false;
    return new Promise(function (resolve) {
      var timer = setTimeout(function () {
        if (!settled) { settled = true; resolve(null); }
      }, RESOLVE_TIMEOUT_MS);

      function done(v) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(v);
      }

      window.fetch(url, { credentials: 'omit' })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(function (data) {
          if (!data || !data.found || typeof data.tracking_url !== 'string') return done(null);
          /* Only ever navigate to https. The server already allowlists the
             host; this is the second, independent check on the client. */
          if (data.tracking_url.indexOf('https://') !== 0) return done(null);
          done(data.tracking_url);
        })
        .catch(function () { done(null); });
    });
  }

  /* Pattern first (instant, offline, unchanged), server second. */
  function resolveTrackingUrl(tn) {
    var local = buildTrackingLink(tn);
    if (local) return Promise.resolve(local);
    return resolveViaServer(tn);
  }

  /* ---------------------------------------------- TASK-5012 query deep link */

  var QS_KEY = 'tracking';
  // The longest live code is 15 chars (DHL ZP + 13 digits). 64 is generous
  // head-room while still refusing an absurd query string outright, so nothing
  // huge ever reaches the input or the carrier patterns.
  var QS_MAX = 64;

  function readTrackingParam(search) {
    var qs = typeof search === 'string'
      ? search
      : ((window.location && window.location.search) || '');
    var raw = '';
    try {
      raw = new URLSearchParams(qs).get(QS_KEY) || '';
    } catch (_) {
      var m = qs.match(/[?&]tracking=([^&#]*)/i);
      if (!m) return '';
      try { raw = decodeURIComponent(m[1].replace(/[+]/g, ' ')); } catch (__) { raw = ''; }
    }
    if (!raw || raw.length > QS_MAX) return '';
    return raw;
  }

  /* Show the existing localized "unrecognised" state and take the visitor to
     the widget. Factored out of deepLinkFromQuery so the synchronous path and
     the server-fallback path end in exactly the same place. */
  function showUnrecognised(input, error) {
    if (!error || !input) return null;
    error.hidden = false;
    input.classList.add('is-invalid');
    if ((window.location.hash || '').toLowerCase() !== '#track-order') {
      // Fragment-only navigation: no reload, no extra history entry, and it
      // arms the existing #track-order handlers (custom-track-anchor-4997.js
      // landing, custom-anchor-autofocus.js focus) which load after this file.
      try { window.location.replace('#track-order'); }
      catch (_) { window.location.hash = 'track-order'; }
    }
    try { input.focus(); } catch (_) {}
    return null;
  }

  /* Returns a Promise of the carrier URL it navigated to, or null. Locale-safe
     by construction: the path is never read, only location.search. */
  function deepLinkFromQuery() {
    var raw = readTrackingParam();
    if (!raw) return Promise.resolve(null);

    var tn = normalize(raw);
    var root  = document.querySelector('[data-ils-track]');
    var input = root && root.querySelector('.ils-track__input');
    var error = root && root.querySelector('.ils-track__error');

    // Prefill first, so the code is already in place whether we leave or stay.
    if (input) input.value = tn;

    function go(url) {
      if (input) input.classList.remove('is-invalid');
      if (error) error.hidden = true;
      try { window.location.replace(url); } catch (_) { window.location.href = url; }
      return url;
    }

    // The pattern path stays SYNCHRONOUS: a recognised number redirects on the
    // same tick it always did, with no network call and no new failure mode.
    var local = buildTrackingLink(tn);
    if (local) return Promise.resolve(go(local));

    return resolveViaServer(tn).then(function (url) {
      if (url) return go(url);
      return showUnrecognised(input, error);
    });
  }

  function wire(root) {
    var input = root.querySelector('.ils-track__input');
    var btn   = root.querySelector('.ils-track__btn');
    var error = root.querySelector('.ils-track__error');
    if (!input || !btn || !error) return;

    var pending = false;

    function open(url) {
      error.hidden = true;
      input.classList.remove('is-invalid');
      window.open(url, '_blank', 'noopener,noreferrer');
    }

    function fail() {
      error.hidden = false;
      input.classList.add('is-invalid');
      try { input.focus(); } catch (_) {}
    }

    function submit() {
      if (pending) return;
      var tn = normalize(input.value);
      input.value = tn;

      // Recognised by pattern: open in the SAME synchronous turn as the click,
      // so the browser still treats it as user-authorised and does not block
      // the popup. This is why the local check must stay first and sync.
      var local = buildTrackingLink(tn);
      if (local) { open(local); return; }

      // Unrecognised: ask the server. A popup opened from the .then() has lost
      // the user gesture and would be blocked, so this path NAVIGATES THE
      // CURRENT TAB instead of opening a new one. The buyer still lands on the
      // carrier's portal; only the tab differs, and only for numbers that
      // previously produced an error and no navigation at all.
      pending = true;
      btn.setAttribute('aria-busy', 'true');
      resolveViaServer(tn).then(function (url) {
        pending = false;
        btn.removeAttribute('aria-busy');
        if (!url) { fail(); return; }
        error.hidden = true;
        input.classList.remove('is-invalid');
        try { window.location.assign(url); } catch (_) { window.location.href = url; }
      });
    }

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      submit();
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        submit();
      }
    });

    input.addEventListener('input', function () {
      if (!error.hidden) {
        error.hidden = true;
        input.classList.remove('is-invalid');
      }
    });
  }

  function init() {
    var nodes = document.querySelectorAll('[data-ils-track]');
    for (var i = 0; i < nodes.length; i++) wire(nodes[i]);
    // After wiring, so the manual handlers exist even if we end up staying.
    deepLinkFromQuery();
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }

  // Expose for verification / Playwright tests.
  window.ILSTrack = {
    normalize: normalize,
    detectCarrier: detectCarrier,
    buildTrackingLink: buildTrackingLink,
    readTrackingParam: readTrackingParam,
    deepLinkFromQuery: deepLinkFromQuery,
    // TASK-5579
    resolveViaServer: resolveViaServer,
    resolveTrackingUrl: resolveTrackingUrl,
    apiBase: apiBase
  };
})();
